import React, { useState, useEffect, useMemo, useRef, JSX } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { FiCheck, FiChevronRight, FiCopy, FiShield } from 'react-icons/fi';
import {
  Field,
  FieldLabel,
  Group,
  TextInput,
  AdornedInput,
  Chip,
  ChipRow,
  SwitchRow,
  PrimaryButton,
  OutlineButton,
  GhostButton,
  Required,
  RequiredChoice,
} from 'components/Onboarding';
import {
  ConfigHeader,
  Segmented,
  SubLabel,
  TwoCol,
  InlineLink,
  Callout,
  NoteLine,
  StatusBanner,
  ActionsRow,
  InlineAction,
  StepsList,
  CalendarSkin,
  TileInfo,
  LoadingNote,
  Sheet,
  SheetHeader,
  SheetRow,
  ToggleGroup,
  FullScreen,
  Overlay,
  Modal,
  BookedModal,
  StayBar,
  StayLabel,
} from './CalendarPricing.styles';
import { bookedStretch, classifyBookedNight, holdsDates, isPaymentPending } from './bookedNight';
import { formatDateString, getOccupiedNightKeys, toYmd } from './calendarDates';
import { buildStays, indexStays, stayAt } from './stayBars';
import CalendarService, { HostCalendarDayDTO } from 'service/CalendarService';
// @ts-ignore — JS module
import PricingService from 'service/PricingService';
// @ts-ignore — JS module
import PaymentService from 'service/PaymentService';
import PropertyService from 'service/PropertyService';
import { formatCurrencyAmount } from 'containers/Confirmation/utils';
import { notification } from 'antd';

import { Value } from 'react-calendar/dist/cjs/shared/types';
import { getUserFriendlyError } from 'library/helpers/errorMessage';
import { CancellationPolicyPicker } from 'components/CancellationPolicyPicker/CancellationPolicyPicker';
import { getCatalog as getCancellationCatalog } from 'service/CancellationPolicyService';
import type { CancellationPolicyTemplate } from 'features/placeSlice';

/** Matches airbnb.com, airbnb.co.in, airbnb.de, etc. */
const isAirbnbUrl = (url: string): boolean => {
  try {
    return /(^|\.)airbnb\.[a-z.]+$/i.test(new URL(url).hostname);
  } catch {
    return false;
  }
};

/** Airbnb's iCal export links look like https://www.airbnb.com/calendar/ical/12345.ics?s=... */
const looksLikeAirbnbIcalUrl = (url: string): boolean => {
  try {
    const { pathname } = new URL(url);
    return /\/calendar\/ical\//i.test(pathname) && /\.ics$/i.test(pathname);
  } catch {
    return false;
  }
};

/** Cycled on the pricing-sync submit button so a 1-2 minute wait doesn't look frozen. */
const PRICING_SYNC_MESSAGES = [
  'Submitting…',
  'Verifying link…',
  'Checking listing…',
  'Fetching pricing…',
  'Still working, hang tight…',
  'Almost there…',
];

/** Outlined icons aligned to default price row height (~44px inputs) */
const CALENDAR_SECTION_ICON_SX = {
  fontSize: 24,
  color: '#ff710d',
} as const;

export type WeekendDays = Record<'fri' | 'sat' | 'sun', boolean>;

/** Host's default weekend when nothing is saved yet. */
export const DEFAULT_WEEKEND_DAYS: WeekendDays = { fri: false, sat: true, sun: true };

/** {fri,sat,sun} booleans -> "sat,sun" / "fri,sat,sun" for the API. */
export const serializeWeekendDays = (d: WeekendDays): string =>
  (['fri', 'sat', 'sun'] as const).filter((k) => d[k]).join(',');

/**
 * "fri,sat,sun" (any order) -> {fri,sat,sun} booleans. A null / empty / unrecognised value
 * falls back to the Sat+Sun default rather than an all-false object (which would disable the
 * whole weekend concept).
 */
export const parseWeekendDays = (raw: unknown): WeekendDays => {
  if (typeof raw !== 'string' || !raw.trim()) return { ...DEFAULT_WEEKEND_DAYS };
  const set = new Set(raw.split(',').map((s) => s.trim().toLowerCase()));
  const parsed = { fri: set.has('fri'), sat: set.has('sat'), sun: set.has('sun') };
  return parsed.fri || parsed.sat || parsed.sun ? parsed : { ...DEFAULT_WEEKEND_DAYS };
};

interface CalendarPricingProps {
  prices?: Record<string, number>;
  setPrices: (prices: Record<string, number>) => void;
  /** Called with the dates the host just saved, so the parent can keep them if it re-syncs. */
  onPricesEdited?: (edited: Record<string, number>) => void;
  defaultPrice?: number;
  setDefaultPrice: React.Dispatch<React.SetStateAction<number | undefined>>;
  weekendDefaultPrice?: number;
  setWeekendDefaultPrice: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  weekdayMinNights: number;
  setWeekdayMinNights: React.Dispatch<React.SetStateAction<number>>;
  weekendMinNights: number;
  setWeekendMinNights: React.Dispatch<React.SetStateAction<number>>;
  selectedWeekendDays: WeekendDays;
  setSelectedWeekendDays: React.Dispatch<React.SetStateAction<WeekendDays>>;
  securityDeposit?: number;
  setSecurityDeposit: React.Dispatch<React.SetStateAction<number | undefined>>;
  propertyId?: string;
  existingSyncUrl?: string;
  // false once the property's been unsynced (PropertyUnsyncAirbnb tool / property.
  // airbnbPriceSyncEnabled) — undefined is treated as true so existing properties predating
  // this flag keep showing their saved link.
  airbnbPriceSyncEnabled?: boolean;
  // true while the backend is quietly fetching this Airbnb-linked listing's price
  fetchingDefaultPrice?: boolean;
}

interface TileContentArgs {
  date: Date;
  view: string;
}

/** Merge YYYY-MM-DD strings into contiguous [from, to] ranges (inclusive). */
const mergeContiguousDateRanges = (
  dateStrings: string[],
): { from: string; to: string }[] => {
  if (!dateStrings.length) return [];
  const sorted = dateStrings
    .filter((d, i, arr) => arr.indexOf(d) === i)
    .sort();
  const ranges: { from: string; to: string }[] = [];
  let rangeStart = sorted[0];
  let rangeEnd = sorted[0];
  const dayMs = 86400000;
  for (let i = 1; i < sorted.length; i++) {
    const prevT = new Date(rangeEnd + 'T00:00:00').getTime();
    const curr = sorted[i];
    const currT = new Date(curr + 'T00:00:00').getTime();
    if (currT - prevT === dayMs) {
      rangeEnd = curr;
    } else {
      ranges.push({ from: rangeStart, to: rangeEnd });
      rangeStart = curr;
      rangeEnd = curr;
    }
  }
  ranges.push({ from: rangeStart, to: rangeEnd });
  return ranges;
};

const resolveGuestName = (b: Record<string, unknown>): string => {
  const guestDetails = b.guestDetails as { guestName?: string } | undefined;
  const user = b.user as
    | { name?: string; firstName?: string; lastName?: string }
    | undefined;
  const name =
    (b.guestName as string) ||
    (b.userName as string) ||
    (b.customerName as string) ||
    (b.bookerName as string) ||
    guestDetails?.guestName ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    user?.name ||
    '';
  return name.trim() || 'Guest';
};

const bookingTotalAmount = (b: Record<string, unknown>): number => {
  const base = Number(b.baseAmount || 0);
  const disc = Number(b.discountAmount || 0);
  const tax = Number(b.taxAmount || 0);
  const fee = Number(b.serviceFeeAmount || 0);
  const add = Number(b.additionalChargesAmount || 0);
  return base - disc + tax + fee + add;
};

const resolveBookedNightRateLabel = (b: Record<string, unknown>): string => {
  const ppn = b.pricePerNight ?? b.perNightPrice ?? b.nightlyPrice;
  if (ppn != null && Number(ppn) > 0) {
    return `${formatCurrencyAmount.format(Number(ppn))} / night`;
  }
  const total = bookingTotalAmount(b);
  const ci = toYmd(String(b.checkIn || b.check_in || ''));
  const co = toYmd(String(b.checkOut || b.check_out || ''));
  const nights = ci ? getOccupiedNightKeys(ci, co || undefined).length : 1;
  const n = Math.max(1, nights);
  if (total > 0) {
    return `${formatCurrencyAmount.format(total / n)} / night (avg. from booking total)`;
  }
  return '—';
};

/** A property's bookings grouped by night they cover (one entry per booking per night), for the bookings that pass `include`. */
const groupBookingsByNight = (
  bookings: Record<string, unknown>[],
  include: (status: unknown) => boolean,
): Record<string, Record<string, unknown>[]> => {
  const map: Record<string, Record<string, unknown>[]> = {};
  const seen: Record<string, Set<string>> = {};
  bookings.forEach((b) => {
    if (!include(b.status)) return;
    const ci = String(b.checkIn || b.check_in || '');
    const co = String(b.checkOut || b.check_out || '');
    const bid = String(b.bookingId ?? b.bookingID ?? b.id ?? `${ci}-${co}-${resolveGuestName(b)}`);
    getOccupiedNightKeys(ci, co || undefined).forEach((d) => {
      if (!map[d]) map[d] = [];
      if (!seen[d]) seen[d] = new Set();
      if (seen[d].has(bid)) return;
      seen[d].add(bid);
      map[d].push(b);
    });
  });
  return map;
};

const bookingMatchesProperty = (
  b: Record<string, unknown>,
  propertyId: string,
): boolean => {
  const candidates = [
    b.propertyId,
    b.propertyID,
    (b.property as { propertyId?: number } | undefined)?.propertyId,
    (b.property as { id?: number } | undefined)?.id,
    b.listingId,
  ];
  return candidates.some(
    (id) => id != null && String(id) === String(propertyId),
  );
};


const SECURITY_DEPOSIT_INFO = (
  <StepsList>
    <span>Collected directly by you from the guest before or at check-in.</span>
    <span>Refunded after check-out according to your property's damage and refund policy.</span>
    <span>Unpaqd does not collect, hold, manage, or mediate this amount.</span>
  </StepsList>
);

const SecurityDepositCard: React.FC<{
  securityDeposit?: number;
  setSecurityDeposit: React.Dispatch<React.SetStateAction<number | undefined>>;
}> = ({ securityDeposit, setSecurityDeposit }) => {
  const [enabled, setEnabled] = React.useState<boolean>(securityDeposit != null);
  const [showInfo, setShowInfo] = React.useState(false);

  React.useEffect(() => {
    if (securityDeposit != null) setEnabled(true);
  }, [securityDeposit]);

  const handleToggle = (next: boolean) => {
    setEnabled(next);
    if (!next) setSecurityDeposit(undefined);
  };

  return (
    <>
      <SwitchRow
        label="Refundable security deposit"
        checked={enabled}
        onChange={handleToggle}
      />
      {enabled && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Refundable Security Deposit Amount">
            <AdornedInput>
              <span className="pre">₹</span>
              <TextInput
                type="number"
                min={0}
                placeholder="e.g. 5,000"
                style={{ paddingLeft: 36 }}
                value={securityDeposit ?? ''}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setSecurityDeposit(undefined);
                  } else {
                    const v = Number(e.target.value);
                    setSecurityDeposit(Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0);
                  }
                }}
              />
            </AdornedInput>
          </Field>
          <NoteLine style={{ margin: 0 }}>Collected directly by you before or at check-in.</NoteLine>
          <InlineLink type="button" onClick={() => setShowInfo((v) => !v)}>
            Learn how this works →
          </InlineLink>
          {showInfo && <Callout>{SECURITY_DEPOSIT_INFO}</Callout>}
          <Callout>
            <FiShield size={20} />
            <div>
              <div className="t">Visible to guests before booking</div>
              <div className="s">Shown on the property page and during checkout.</div>
            </div>
          </Callout>
        </div>
      )}
    </>
  );
};

const CalendarPricing: React.FC<CalendarPricingProps> = ({
  prices = {},
  setPrices,
  onPricesEdited,
  defaultPrice,
  setDefaultPrice,
  weekendDefaultPrice,
  setWeekendDefaultPrice,
  weekdayMinNights,
  setWeekdayMinNights,
  weekendMinNights,
  setWeekendMinNights,
  selectedWeekendDays,
  setSelectedWeekendDays,
  securityDeposit,
  setSecurityDeposit,
  propertyId,
  existingSyncUrl,
  airbnbPriceSyncEnabled,
  fetchingDefaultPrice,
}) => {
  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const hostPropertyId = query.get('id');
  const resolvePropertyId = (): string | null =>
    hostPropertyId ||
    propertyId ||
    localStorage.getItem('id') ||
    localStorage.getItem('newPropertId');
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [previousDefaultPrice, setPreviousDefaultPrice] = useState<
    number | undefined
  >(defaultPrice);
  const [previousWeekendDefaultPrice, setPreviousWeekendDefaultPrice] =
    useState<number | undefined>(weekendDefaultPrice);
  const [bulkPrice, setBulkPrice] = useState<string>('');
  const [actionsModalOpen, setActionsModalOpen] = useState(false);
  const [priceInputOpen, setPriceInputOpen] = useState(false);
  const [customSettingsOpen, setCustomSettingsOpen] = useState(false);
  const [calendarActionLoading, setCalendarActionLoading] = useState(false);
  const [minNightsBulk, setMinNightsBulk] = useState<number>(1);
  const [cancellationPolicyBulk, setCancellationPolicyBulk] = useState<string>('');
  const [cancellationCatalog, setCancellationCatalog] = useState<
    CancellationPolicyTemplate[]
  >([]);
  const [apiCalendarPriceMap, setApiCalendarPriceMap] = useState<
    Record<string, number>
  >({});
  const [apiDefaultPrice, setApiDefaultPrice] = useState<number | null>(null);
  const [apiWeekendDefaultPrice, setApiWeekendDefaultPrice] = useState<
    number | null
  >(null);
  const [activeStartDate, setActiveStartDate] = useState<Date>(new Date());
  const [calendarData, setCalendarData] = useState<
    Record<string, HostCalendarDayDTO>
  >({});
  const calendarDataRef = useRef(calendarData);
  calendarDataRef.current = calendarData;
  // Prices the host saved from the date sheet during this visit. The server accepted them, but the
  // background calendar reload can still return the previous value for a moment — without this the
  // old price snaps back onto the calendar right after saving.
  const locallyEditedPricesRef = useRef<Record<string, number>>({});
  // Set true when weekday/weekend min-nights are loaded from the API, so the "apply to the next
  // 3 months" effect skips the run that hydration triggers (it should reflect the saved value,
  // not immediately rewrite calendar rows with it).
  const minNightsHydratedRef = useRef(false);
  // False until the initial pricing snapshot has been fetched. The "apply min-nights to the next
  // 3 months" effect must not run before this: on mount weekday/weekendMinNights still hold the
  // parent's default of 1, and spreading that would overwrite (and persist to the backend) the
  // real saved values that the snapshot is about to load.
  const [pricingSnapshotLoaded, setPricingSnapshotLoaded] = useState(false);
  // Tracks the debounced min-nights sync below so it can be flushed (rather than dropped) if
  // this component unmounts while a push is still pending.
  const pendingMinNightsSyncRef = useRef<{
    timer: number | null;
    run: (() => void) | null;
  }>({ timer: null, run: null });
  const [isLoadingCalendar, setIsLoadingCalendar] = useState<boolean>(false);
  // weekdayMinNights / weekendMinNights are owned by the parent (PropertyListingModern) so they
  // can be persisted in the pricing save payload and rehydrated on a later edit.
  const [hostBookings, setHostBookings] = useState<Record<string, unknown>[]>(
    [],
  );
  // Whether the host's bookings were loaded (an empty list is then a real "no Unpaqd bookings", not a failed load)
  const [hostBookingsLoaded, setHostBookingsLoaded] = useState(false);
  const [bookedDetailModal, setBookedDetailModal] = useState<{
    open: boolean;
    dateStr: string | null;
  }>({ open: false, dateStr: null });

  const { pricingDetails } = useSelector((state: any) => ({
    pricingDetails: state.pricingDetails,
  }));

  const todayStart = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // Host-chosen weekend days (owned by the parent so they can be persisted in the pricing save
  // payload and rehydrated on a later edit). Note: Date.getDay() -> 0=Sunday, 5=Friday, 6=Saturday.

  // ===== iCal availability subscription state =====
  const [icalUrl, setIcalUrl] = useState<string>('');
  const [savedIcalUrl, setSavedIcalUrl] = useState<string>('');
  const [icalSubmitting, setIcalSubmitting] = useState<boolean>(false);
  const [icalReloading, setIcalReloading] = useState<boolean>(false);
  const [icalStepsOpen, setIcalStepsOpen] = useState<boolean>(false);

  // ===== Outbound Unpaqd iCal export state =====
  const [exportUrl, setExportUrl] = useState<string>('');
  const [exportGenerating, setExportGenerating] = useState<boolean>(false);
  const [exportCopied, setExportCopied] = useState<boolean>(false);

  // ===== Pricing-URL (other-platform listing URL) state =====
  const [pricingUrl, setPricingUrl] = useState<string>('');
  const [savedPricingUrl, setSavedPricingUrl] = useState<string>('');
  // null = not submitted yet. 'SYNCED' = Airbnb URL, price synced immediately.
  // 'PENDING_REVIEW' (or any other value, e.g. if the call fails) = queued for admin review.
  const [pricingUrlResult, setPricingUrlResult] = useState<'SYNCED' | 'PENDING_REVIEW' | null>(null);
  const [pricingUrlLoading, setPricingUrlLoading] = useState<boolean>(false);
  const [pricingSyncMsgIndex, setPricingSyncMsgIndex] = useState<number>(0);

  // Airbnb pricing sync can take 1-2 minutes (the backend fetches + parses the listing
  // synchronously), so cycle the button copy every 2s — a static "Submitting…" for two
  // minutes straight reads as a frozen/broken button.
  useEffect(() => {
    if (!pricingUrlLoading) {
      setPricingSyncMsgIndex(0);
      return;
    }
    const id = window.setInterval(() => {
      setPricingSyncMsgIndex((i) => (i + 1) % PRICING_SYNC_MESSAGES.length);
    }, 2000);
    return () => window.clearInterval(id);
  }, [pricingUrlLoading]);

  // Pre-fill the pricing-sync URL field so a previously submitted URL is always visible,
  // never a blank box — same intent as the iCal pre-fill above. We can't yet confirm the
  // backend echoes this URL back via property details, so localStorage is the guaranteed
  // source of truth; `existingSyncUrl` (property.bnbUrl) is a best-effort fallback in case
  // it does, e.g. when the host revisits from a different browser/device.
  useEffect(() => {
    const pid = resolvePropertyId();
    if (!pid) return;

    // The "Unsync Airbnb Pricing" tool leaves this marker so we can hide the now-inactive link
    // on this device even when the property-details API doesn't echo an airbnbPriceSyncEnabled
    // flag. An explicit server `true` (re-synced elsewhere) overrides and clears the stale marker.
    let locallyDisabled = false;
    try {
      locallyDisabled =
        localStorage.getItem(`airbnbPriceSyncDisabled_${pid}`) === '1';
      if (locallyDisabled && airbnbPriceSyncEnabled === true) {
        localStorage.removeItem(`airbnbPriceSyncDisabled_${pid}`);
        locallyDisabled = false;
      }
    } catch (_) {
      // localStorage unavailable — fall back to the prop alone
    }

    // Once explicitly unsynced, the link must disappear from this field regardless of what's
    // cached — otherwise it keeps showing a link that's no longer actually syncing anything,
    // until the host pastes and resubmits it. Wipe both the in-memory state and the localStorage
    // cache (the field's usual pre-fill source) so a later revisit can't resurrect it either.
    if (airbnbPriceSyncEnabled === false || locallyDisabled) {
      try {
        localStorage.removeItem(`pricingSyncUrl_${pid}`);
        localStorage.removeItem(`pricingSyncStatus_${pid}`);
        // Persist the disabled state on this device so it survives reloads even if the
        // property-details API never reports airbnbPriceSyncEnabled=false.
        localStorage.setItem(`airbnbPriceSyncDisabled_${pid}`, '1');
      } catch (_) {
        // localStorage unavailable — nothing to clean up
      }
      setPricingUrl('');
      setSavedPricingUrl('');
      setPricingUrlResult(null);
      return;
    }

    try {
      const storedUrl = localStorage.getItem(`pricingSyncUrl_${pid}`) || '';
      const storedStatus = localStorage.getItem(`pricingSyncStatus_${pid}`);
      if (storedUrl) {
        setPricingUrl(storedUrl);
        setSavedPricingUrl(storedUrl);
        if (storedStatus === 'SYNCED' || storedStatus === 'PENDING_REVIEW') {
          setPricingUrlResult(storedStatus);
        }
        return;
      }
    } catch (_) {
      // localStorage unavailable — fall through to the bnbUrl fallback below
    }
    if (existingSyncUrl) {
      setPricingUrl(existingSyncUrl);
      setSavedPricingUrl(existingSyncUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, existingSyncUrl, airbnbPriceSyncEnabled]);

  // Pre-fill the Airbnb iCal field with whatever URL the host previously submitted, so they
  // see "this is what's stored" instead of an empty box every time they revisit the page.
  // Submit only appears once the input differs from `savedIcalUrl`.
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      try {
        const subs = await CalendarService.getIcalSubscriptions(propertyId);
        if (cancelled) return;
        const existing = subs.find((s) => s.source === 'ICAL_AIRBNB' && s.enabled);
        if (existing?.feedUrl) {
          setIcalUrl(existing.feedUrl);
          setSavedIcalUrl(existing.feedUrl);
        }
      } catch {
        // Silent — service already logs; an empty form is acceptable fallback.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  const selectedWeekendDayNumbers = useMemo(() => {
    const days: number[] = [];
    if (selectedWeekendDays.sun) days.push(0);
    if (selectedWeekendDays.fri) days.push(5);
    if (selectedWeekendDays.sat) days.push(6);
    return days;
  }, [selectedWeekendDays]);

  const toggleWeekendDay = (key: 'fri' | 'sat' | 'sun') => {
    setSelectedWeekendDays((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // Always keep at least one weekend day selected.
      const anySelected = Object.values(next).some(Boolean);
      return anySelected ? next : prev;
    });
  };

  const calendarMaxDate = React.useMemo(() => {
    const dates = Object.keys(calendarData);
    if (dates.length === 0) {
      const fallback = new Date();
      fallback.setFullYear(fallback.getFullYear() + 1);
      return fallback;
    }
    const latest = dates.sort().pop()!;
    return new Date(latest + 'T23:59:59');
  }, [calendarData]);

  // Fetch calendar data on component mount
  useEffect(() => {
    const fetchCalendarData = async () => {
      const currentPropertyId = resolvePropertyId();
      if (!currentPropertyId) return;

      setIsLoadingCalendar(true);
      try {
        const response =
          await CalendarService.getHostCalendar(currentPropertyId);

        const result = response.result as
          | HostCalendarDayDTO[]
          | { dates?: HostCalendarDayDTO[] };
        const days = Array.isArray(result) ? result : result?.dates || [];
        if (
          response.responseInformation?.responseCode === 0 &&
          days.length > 0
        ) {
          const calendarMap: Record<string, HostCalendarDayDTO> = {};
          days.forEach((day) => {
            calendarMap[day.date] = day;
          });
          setCalendarData(calendarMap);
        } else {
          console.log('Calendar not found/empty, creating default calendar...');
          await createDefaultCalendar(currentPropertyId);
        }
      } catch (error: any) {
        console.error('Error fetching calendar:', error);
        if (error.response?.status === 404 || error.response?.status === 400) {
          console.log('Calendar API error, creating default calendar...');
          await createDefaultCalendar(currentPropertyId);
        }
      } finally {
        setIsLoadingCalendar(false);
      }
    };

    fetchCalendarData();
  }, [propertyId, hostPropertyId]);

  useEffect(() => {
    getCancellationCatalog()
      .then(setCancellationCatalog)
      .catch(() => setCancellationCatalog([]));
  }, []);

  const reloadCalendarData = async (): Promise<void> => {
    const currentPropertyId = resolvePropertyId();
    if (!currentPropertyId) return;
    try {
      const response = await CalendarService.getHostCalendar(currentPropertyId);
      const result = response.result as
        | HostCalendarDayDTO[]
        | { dates?: HostCalendarDayDTO[] };
      const days = Array.isArray(result) ? result : result?.dates || [];
      if (response.responseInformation?.responseCode === 0 && days.length > 0) {
        const calendarMap: Record<string, HostCalendarDayDTO> = {};
        days.forEach((day) => {
          calendarMap[day.date] = day;
        });
        setCalendarData(calendarMap);
      }
    } catch (_) {
      // ignore
    }
  };

  const fetchPricingSnapshot = async (): Promise<void> => {
    const currentPropertyId = resolvePropertyId();
    if (!currentPropertyId) return;
    try {
      const response: any = await PricingService.getPropertyPricingDetails(
        currentPropertyId,
      );
      const result = response?.result || {};
      // If price sync was stopped for this property (the "Unsync Airbnb Pricing" tool), drop the
      // saved listing link here too — some backends surface the flag on the pricing payload
      // rather than property details. Only acts on an explicit `false`.
      if (result?.airbnbPriceSyncEnabled === false) {
        try {
          localStorage.removeItem(`pricingSyncUrl_${currentPropertyId}`);
          localStorage.removeItem(`pricingSyncStatus_${currentPropertyId}`);
          localStorage.setItem(`airbnbPriceSyncDisabled_${currentPropertyId}`, '1');
        } catch (_) {
          // localStorage unavailable — nothing to clean up
        }
        setPricingUrl('');
        setSavedPricingUrl('');
        setPricingUrlResult(null);
      } else if (result?.airbnbPriceSyncEnabled === true) {
        // Re-synced (possibly from another device) — clear the stale local disable marker.
        try {
          localStorage.removeItem(`airbnbPriceSyncDisabled_${currentPropertyId}`);
        } catch (_) {
          // ignore
        }
      }
      const calendarPrices = Array.isArray(result?.propertyCalendarPrices)
        ? result.propertyCalendarPrices
        : [];
      const nextMap = calendarPrices.reduce(
        (acc: Record<string, number>, item: any) => {
          if (item?.priceDate && item?.pricePerNight != null) {
            acc[String(item.priceDate)] = Number(item.pricePerNight);
          }
          return acc;
        },
        {},
      );
      setApiCalendarPriceMap(nextMap);
      if (typeof result?.defaultPrice === 'number') {
        setApiDefaultPrice(result.defaultPrice);
        setDefaultPrice(result.defaultPrice);
      } else {
        setApiDefaultPrice(null);
      }
      const savedWeekendDefaultPrice =
        typeof result?.weekendDefaultPrice === 'number'
          ? result.weekendDefaultPrice
          : null;
      setApiWeekendDefaultPrice(savedWeekendDefaultPrice);
      // Rehydrate the "Default Price Per Night - Weekends" input from the persisted value so a
      // revisit shows what the host last entered (this used to be left blank — the value was
      // spread onto per-day rows but never stored as a template).
      if (savedWeekendDefaultPrice != null) {
        setWeekendDefaultPrice(savedWeekendDefaultPrice);
      }
      // Same for the weekday / weekend minimum-nights inputs. Flag the load so the "apply min
      // nights to the next 3 months" effect skips its next run and doesn't re-push these
      // freshly-loaded values back onto the calendar rows.
      if (
        typeof result?.weekdayMinNights === 'number' &&
        result.weekdayMinNights >= 1
      ) {
        minNightsHydratedRef.current = true;
        setWeekdayMinNights(result.weekdayMinNights);
      }
      if (
        typeof result?.weekendMinNights === 'number' &&
        result.weekendMinNights >= 1
      ) {
        minNightsHydratedRef.current = true;
        setWeekendMinNights(result.weekendMinNights);
      }
      // Rehydrate the weekend-day pills (Fri/Sat/Sun). Only when the server actually has a value —
      // otherwise leave whatever's there (default Sat+Sun) untouched. Flag it as a hydration too
      // so the min-nights re-spread effect (keyed on the weekend-day set) skips its next run.
      if (typeof result?.weekendDays === 'string' && result.weekendDays.trim()) {
        minNightsHydratedRef.current = true;
        setSelectedWeekendDays(parseWeekendDays(result.weekendDays));
      }
      if (typeof result?.securityDeposit === 'number') {
        setSecurityDeposit(result.securityDeposit);
      }
    } catch (_) {
      // redux / props still apply
    } finally {
      // Unblock the min-nights spread effect now that the saved values (if any) have been
      // hydrated above. A run it makes from here on reflects a real host edit, not mount defaults.
      setPricingSnapshotLoaded(true);
    }
  };

  useEffect(() => {
    fetchPricingSnapshot();
  }, [propertyId, hostPropertyId]);

  useEffect(() => {
    const pid = resolvePropertyId();
    if (!pid) return;
    PaymentService.getBookingsForHost()
      .then((res: { result?: unknown; responseInformation?: { responseCode?: number } }) => {
        const list = Array.isArray(res?.result) ? res.result : [];
        setHostBookings(list as Record<string, unknown>[]);
        // a successful answer with a list is "loaded", whatever the code (no bookings can come back as a "not found" code)
        setHostBookingsLoaded(Array.isArray(res?.result));
      })
      .catch(() => {
        setHostBookings([]);
        setHostBookingsLoaded(false);
      });
  }, [propertyId, hostPropertyId]);

  // This property's bookings, once; the maps below are all made from this list
  const propertyBookings = useMemo(() => {
    const pid = resolvePropertyId();
    return pid
      ? hostBookings.map((raw) => raw as Record<string, unknown>).filter((b) => bookingMatchesProperty(b, pid))
      : [];
  }, [hostBookings, propertyId, hostPropertyId]);

  // bookings that hold their nights (cancelled, failed or never paid ones do not)
  const bookingsByDate = useMemo(() => groupBookingsByNight(propertyBookings, holdsDates), [propertyBookings]);
  // bookings still being paid for: they hold no dates, but the calendar may already show their nights as booked
  const pendingByDate = useMemo(() => groupBookingsByNight(propertyBookings, isPaymentPending), [propertyBookings]);
  // cancelled, failed or expired bookings: a backend that does not say who booked a night can still show it as booked
  const droppedByDate = useMemo(
    () => groupBookingsByNight(propertyBookings, (status) => !holdsDates(status) && !isPaymentPending(status)),
    [propertyBookings],
  );

  // The bars drawn across booked nights: our bookings with the guest, other platforms' as "Booked"
  const todayYmd = formatDateString(todayStart);
  const stays = useMemo(() => {
    const bookings = propertyBookings
      .filter((b) => holdsDates(b.status))
      .map((b) => {
        const name = resolveGuestName(b);
        return {
          checkIn: toYmd(String(b.checkIn || b.check_in || '')) || '',
          checkOut: toYmd(String(b.checkOut || b.check_out || '')) || '',
          guestName: name === 'Guest' ? '' : name,
          guests: Number(b.adults || 0) + Number(b.children || 0),
        };
      });
    return buildStays(calendarData, bookings, todayYmd);
  }, [propertyBookings, calendarData, todayYmd]);
  const stayIndex = useMemo(() => indexStays(stays, todayYmd), [stays, todayYmd]);

  const getDisplayPriceForDate = (
    date: Date,
    dateString: string,
  ): number | null => {
    const locallyEdited = locallyEditedPricesRef.current[dateString];
    if (locallyEdited != null && !Number.isNaN(Number(locallyEdited))) {
      return Number(locallyEdited);
    }
    const dayDataPrice = calendarData[dateString]?.price;
    if (dayDataPrice != null && !Number.isNaN(Number(dayDataPrice))) {
      return Number(dayDataPrice);
    }
    const apiOverride = apiCalendarPriceMap?.[dateString];
    if (apiOverride != null && !Number.isNaN(Number(apiOverride))) {
      return Number(apiOverride);
    }
    const fromProps = prices?.[dateString];
    if (fromProps != null && !Number.isNaN(Number(fromProps))) {
      return Number(fromProps);
    }
    const overridePrice =
      pricingDetails?.propertyCalendarPrices?.[dateString];
    if (overridePrice != null && !Number.isNaN(Number(overridePrice))) {
      return Number(overridePrice);
    }
    const day = date.getDay();
    const weekendDef = Number(
      pricingDetails?.weekendDefaultPrice ?? apiWeekendDefaultPrice ?? 0,
    );
    const def = Number(pricingDetails?.defaultPrice ?? apiDefaultPrice ?? 0);
    const wk = weekendDefaultPrice != null ? Number(weekendDefaultPrice) : null;
    const dp = defaultPrice != null ? Number(defaultPrice) : null;
    // Use the host's actual selected weekend days (Fri/Sat/Sun toggles), not a hardcoded
    // Sat/Sun check — otherwise a day like Friday that's selected as weekend (with Saturday
    // deselected) keeps falling through to the weekday default here.
    if (
      selectedWeekendDayNumbers.includes(day) &&
      (weekendDef > 0 || (wk != null && wk > 0))
    ) {
      return wk != null && wk > 0 ? wk : weekendDef;
    }
    if (dp != null && dp > 0) return dp;
    if (def > 0) return def;
    return null;
  };

  /** Resolved minimum stay for a night (API row first, else defaults from weekday/weekend rules). */
  const getDisplayMinNightsForDate = (
    date: Date,
    dateString: string,
  ): number | null => {
    const fromCalendar = calendarData[dateString]?.minNights;
    if (typeof fromCalendar === 'number' && fromCalendar >= 1) {
      return fromCalendar;
    }
    const dayOfWeek = date.getDay();
    const isWeekendDay = selectedWeekendDayNumbers.includes(dayOfWeek);
    const fallback = isWeekendDay ? weekendMinNights : weekdayMinNights;
    if (typeof fallback === 'number' && fallback >= 1) return fallback;
    return null;
  };

  const dayStatus = (dateStr: string): HostCalendarDayDTO['status'] => {
    return calendarData[dateStr]?.status ?? 'AVAILABLE';
  };

  const createDefaultCalendar = async (currentPropertyId: string) => {
    try {
      const response = await CalendarService.createDefaultCalendar(
        currentPropertyId,
        {
          defaultMinNights: weekdayMinNights,
        },
      );

      if (response.responseInformation?.responseCode === 0) {
        notification.success({
          message: 'Calendar Created',
          description: 'Default calendar has been created for your property.',
        });
        // Fetch calendar data again
        const calendarResponse =
          await CalendarService.getHostCalendar(currentPropertyId);
        const res = calendarResponse.result as
          | HostCalendarDayDTO[]
          | { dates?: HostCalendarDayDTO[] };
        const days = Array.isArray(res) ? res : res?.dates || [];
        if (days.length > 0) {
          const calendarMap: Record<string, HostCalendarDayDTO> = {};
          days.forEach((day) => {
            calendarMap[day.date] = day;
          });
          setCalendarData(calendarMap);
        }
      }
    } catch (error) {
      console.error('Error creating default calendar:', error);
    }
  };

  // The weekday-default and weekend-default effects both run when the default price changes. The
  // second one used to start from the `prices` of the render, so it wrote that older map back over
  // what the first had just set, and the new weekday price never reached the calendar. Both now
  // build on this ref, which the first updates.
  const latestPricesRef = useRef(prices);
  latestPricesRef.current = prices || {};

  useEffect(() => {
    if (defaultPrice && previousDefaultPrice) {
      const newPrices: Record<string, number> = { ...latestPricesRef.current };
      const today = new Date();
      const endDate = new Date(today);
      endDate.setMonth(today.getMonth() + 3);

      for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = formatDateString(d);
        if (newPrices[dateStr] === previousDefaultPrice) {
          newPrices[dateStr] = defaultPrice;
        }
      }
      latestPricesRef.current = newPrices;
      setPrices(newPrices);
      setPreviousDefaultPrice(defaultPrice);
    } else if (defaultPrice && !previousDefaultPrice) {
      const newPrices: Record<string, number> = { ...latestPricesRef.current };
      const today = new Date();
      const endDate = new Date(today);
      endDate.setMonth(today.getMonth() + 3);

      for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dateStr = formatDateString(d);
        // Only fill gaps — a default price arriving after load (e.g. derived from an
        // Airbnb import) must not overwrite dates that already have their own price.
        if (newPrices[dateStr] == null) newPrices[dateStr] = defaultPrice;
      }

      latestPricesRef.current = newPrices;
      setPrices(newPrices);
      setPreviousDefaultPrice(defaultPrice);
    }
  }, [defaultPrice]);

  // Apply or update weekend-only default price (Sat & Sun) for the next 3 months
  useEffect(() => {
    if (!weekendDefaultPrice && !previousWeekendDefaultPrice) return;
    if (!defaultPrice && defaultPrice !== 0) return;

    const today = new Date();
    const endDate = new Date(today);
    endDate.setMonth(today.getMonth() + 3);

    const newPrices: Record<string, number> = { ...latestPricesRef.current };

    for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dayOfWeek = d.getDay(); // 0 = Sunday, 6 = Saturday
      const isWeekendDay = selectedWeekendDayNumbers.includes(dayOfWeek);

      const dateStr = formatDateString(d);

      // Apply weekend default price only to selected weekend days.
      // Preserve custom prices (we only overwrite if it still matches default or the previous weekend default).
      if (weekendDefaultPrice != null) {
        const current = newPrices[dateStr];
        if (isWeekendDay) {
          if (
            current == null ||
            current === defaultPrice ||
            (previousWeekendDefaultPrice != null &&
              current === previousWeekendDefaultPrice)
          ) {
            newPrices[dateStr] = weekendDefaultPrice;
          }
        } else {
          // If a date previously had weekend price but is now deselected, revert it.
          if (
            previousWeekendDefaultPrice != null &&
            current === previousWeekendDefaultPrice
          ) {
            newPrices[dateStr] = defaultPrice;
          }
        }
      }
    }

    setPrices(newPrices);
    if (weekendDefaultPrice != null) {
      setPreviousWeekendDefaultPrice(weekendDefaultPrice);
    }
  }, [
    weekendDefaultPrice,
    previousWeekendDefaultPrice,
    defaultPrice,
    prices,
    selectedWeekendDayNumbers,
    setPrices,
  ]);

  // Apply weekday / weekend minimum nights to all days in the next 3 months (batched by range).
  // The push itself is debounced (so rapid input edits don't spam the API), but the timer is
  // tracked in a ref rather than cleared unconditionally on cleanup: if the host saves and
  // navigates away (unmounting this component) while a sync is still pending, the effect below
  // flushes it immediately instead of silently dropping it — otherwise a fast "edit then Update
  // Settings" leaves the per-day calendar rows stuck at their old minNights.
  useEffect(() => {
    // Don't spread anything until the initial pricing snapshot has loaded. Before that,
    // weekday/weekendMinNights still hold the parent's default of 1, and a mount-time run would
    // schedule a debounced push that later fires with those stale values — overwriting (and
    // persisting) the real saved min-nights the snapshot loads a moment later.
    if (!pricingSnapshotLoaded) return;
    // Skip the run triggered by hydrating these from the API — the saved value is already
    // reflected on the calendar rows; only host edits after load should re-spread.
    if (minNightsHydratedRef.current) {
      minNightsHydratedRef.current = false;
      // A run scheduled by an earlier pass may still be pending (its debounce hasn't fired).
      // Drop it here rather than let it run with pre-hydration values.
      if (pendingMinNightsSyncRef.current.timer != null) {
        clearTimeout(pendingMinNightsSyncRef.current.timer);
        pendingMinNightsSyncRef.current.timer = null;
        pendingMinNightsSyncRef.current.run = null;
      }
      return;
    }
    const applyRules = async () => {
      const currentPropertyId = resolvePropertyId();
      if (!currentPropertyId) return;
      if (weekdayMinNights < 1 || weekendMinNights < 1) return;

      const today = new Date();
      const endDate = new Date(today);
      endDate.setMonth(today.getMonth() + 3);

      const toUpdateByMin: Record<number, string[]> = {};

      for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay();
        const isWeekendDay = selectedWeekendDayNumbers.includes(dayOfWeek);
        const desired = isWeekendDay ? weekendMinNights : weekdayMinNights;
        const dateStr = formatDateString(d);
        const current = calendarDataRef.current[dateStr]?.minNights;
        if (typeof current === 'number' && current === desired) continue;

        if (!toUpdateByMin[desired]) toUpdateByMin[desired] = [];
        toUpdateByMin[desired].push(dateStr);
      }

      const updates: Array<Promise<any>> = [];
      Object.entries(toUpdateByMin).forEach(([desiredStr, dateKeys]) => {
        const desired = Number(desiredStr);
        if (!dateKeys.length) return;
        mergeContiguousDateRanges(dateKeys).forEach(({ from, to }) => {
          updates.push(
            CalendarService.updateRules(currentPropertyId, {
              from,
              to,
              minNights: desired,
            }),
          );
        });
      });

      if (updates.length === 0) return;

      await Promise.allSettled(updates);

      try {
        const response = await CalendarService.getHostCalendar(
          currentPropertyId,
        );
        const result = response.result as HostCalendarDayDTO[] | { dates?: HostCalendarDayDTO[] };
        const days = Array.isArray(result) ? result : result?.dates || [];
        const calendarMap: Record<string, HostCalendarDayDTO> = {};
        days.forEach((day) => {
          calendarMap[day.date] = day;
        });
        setCalendarData(calendarMap);
      } catch (_) {
        // ignore refresh failures
      }
    };

    if (pendingMinNightsSyncRef.current.timer != null) {
      clearTimeout(pendingMinNightsSyncRef.current.timer);
    }
    pendingMinNightsSyncRef.current.run = applyRules;
    pendingMinNightsSyncRef.current.timer = window.setTimeout(() => {
      pendingMinNightsSyncRef.current.timer = null;
      applyRules();
    }, 600);
  }, [
    weekdayMinNights,
    weekendMinNights,
    selectedWeekendDayNumbers,
    hostPropertyId,
    propertyId,
    pricingSnapshotLoaded,
  ]);

  // Flush a still-pending min-nights sync on unmount (e.g. the host clicked Update Settings
  // moments after editing, before the 600ms debounce above fired) instead of losing it.
  useEffect(() => {
    return () => {
      const pending = pendingMinNightsSyncRef.current;
      if (pending.timer != null) {
        clearTimeout(pending.timer);
        pending.timer = null;
        pending.run?.();
      }
    };
  }, []);

  const handleDateClick = (value: Value): void => {
    if (value instanceof Date) {
      if (value < todayStart) {
        return;
      }

      const dateStr = formatDateString(value);
      if (dayStatus(dateStr) === 'BOOKED') {
        setBookedDetailModal({ open: true, dateStr });
        return;
      }

      setSelectedDates((prev) => {
        if (prev.includes(dateStr)) {
          return prev.filter((d) => d !== dateStr);
        }
        return [...prev, dateStr];
      });
    }
  };

  const openActionsModal = (): void => {
    if (selectedDates.length === 0) return;
    const firstPriced = selectedDates.find((d) => {
      const v = getDisplayPriceForDate(new Date(d + 'T00:00:00'), d);
      return v != null;
    });
    const hint =
      firstPriced != null
        ? String(
            getDisplayPriceForDate(
              new Date(firstPriced + 'T00:00:00'),
              firstPriced,
            ) ?? '',
          )
        : '';
    setBulkPrice(hint && hint !== 'null' ? hint : '');
    setMinNightsBulk(1);
    setActionsModalOpen(true);
  };

  const closeActionsModal = (): void => {
    setActionsModalOpen(false);
  };

  const handleBulkSetPrice = async (): Promise<void> => {
    if (!bulkPrice || selectedDates.length === 0) {
      notification.warning({
        message: 'Price required',
        description: 'Enter a price for the selected dates.',
      });
      return;
    }
    const num = Number(bulkPrice);
    if (!Number.isFinite(num) || num < 0) {
      notification.warning({ message: 'Invalid price' });
      return;
    }

    // 40% minimum rule: selected dates can be weekdays, weekends, or a mix.
    const hasWeekday = selectedDates.some(
      (d) => !selectedWeekendDayNumbers.includes(new Date(d).getDay()),
    );
    const hasWeekend = selectedDates.some((d) =>
      selectedWeekendDayNumbers.includes(new Date(d).getDay()),
    );
    const effectiveWeekendBase = weekendDefaultPrice ?? defaultPrice;
    const weekdayMin = defaultPrice != null ? defaultPrice * 0.4 : null;
    const weekendMin =
      effectiveWeekendBase != null ? effectiveWeekendBase * 0.4 : null;

    if (hasWeekday && weekdayMin != null && num < weekdayMin) {
      notification.warning({
        message: 'Price too low',
        description: `Weekday price cannot be less than 40% of your default weekday price (₹${Math.ceil(weekdayMin)}).`,
        duration: 5,
      });
      return;
    }
    if (hasWeekend && weekendMin != null && num < weekendMin) {
      notification.warning({
        message: 'Price too low',
        description: `Weekend price cannot be less than 40% of your default weekend price (₹${Math.ceil(weekendMin)}).`,
        duration: 5,
      });
      return;
    }

    const pid = resolvePropertyId();
    if (!pid) {
      notification.error({ message: 'Property not found', description: 'Could not identify the property. Please reload the page.' });
      return;
    }

    setCalendarActionLoading(true);
    try {
      const payload = [
        {
          propertyId: Number(pid),
          prices: selectedDates.map((date) => ({ date, pricePerNight: num })),
          // Only these dates change; without this the server replaces the whole calendar and
          // earlier edits to other dates are lost.
          mergeMode: true,
          // Without this the server treats the write as an Airbnb-scraper price.
          source: 'HOST' as const,
        },
      ];
      const syncRes = (await PropertyService.syncCalendarPricing(
        payload,
      )) as {
        _httpStatus?: number;
        responseInformation?: { responseCode?: number; responseMessage?: string };
        message?: string;
      };
      // The service returns error bodies instead of throwing, so check explicitly — otherwise a
      // failed save looks successful and the old price silently comes back on reload.
      if (
        syncRes?._httpStatus ||
        syncRes?.responseInformation?.responseCode !== 0
      ) {
        throw new Error(
          syncRes?.responseInformation?.responseMessage ||
            syncRes?.message ||
            (syncRes?._httpStatus
              ? `Server rejected the request (HTTP ${syncRes._httpStatus})`
              : 'Server did not confirm the save'),
        );
      }

      // Update the prices prop in parent state
      const newPrices: Record<string, number> = { ...prices };
      const editedPrices: Record<string, number> = {};
      selectedDates.forEach((dateStr) => {
        newPrices[dateStr] = num;
        editedPrices[dateStr] = num;
        locallyEditedPricesRef.current[dateStr] = num;
      });
      setPrices(newPrices);
      onPricesEdited?.(editedPrices);

      // Patch calendarData directly — it has the highest display priority in
      // getDisplayPriceForDate and would otherwise keep showing the old price
      // even after the API save succeeds.
      setCalendarData((prev) => {
        const updated = { ...prev };
        selectedDates.forEach((dateStr) => {
          updated[dateStr] = {
            ...(updated[dateStr] ?? { date: dateStr }),
            price: num,
          } as HostCalendarDayDTO;
        });
        return updated;
      });

      setBulkPrice('');
      setSelectedDates([]);
      closeActionsModal();
      setPriceInputOpen(false);
      notification.success({ message: 'Prices updated for selected dates' });

      // Re-sync from server in background to get authoritative data
      reloadCalendarData().catch(() => {});
      fetchPricingSnapshot().catch(() => {});
    } catch (error: unknown) {
      const err = error as { message?: string };
      notification.error({
        message: 'Failed to update prices',
        description: err?.message || 'Could not save the prices. Please try again.',
      });
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const handleBulkBlock = async (): Promise<void> => {
    const pid = resolvePropertyId();
    if (!pid || selectedDates.length === 0) return;
    const eligible = selectedDates.filter((d) => {
      const s = dayStatus(d);
      return s !== 'BLOCKED' && s !== 'BOOKED';
    });
    if (!eligible.length) {
      notification.warning({
        message: 'Nothing to block',
        description: 'Selected dates are already blocked or booked.',
      });
      return;
    }
    setCalendarActionLoading(true);
    try {
      const ranges = mergeContiguousDateRanges(eligible);
      await Promise.all(ranges.map((r) => CalendarService.blockDates(pid, r)));
      notification.success({ message: 'Dates blocked' });
      await reloadCalendarData();
      setSelectedDates([]);
      closeActionsModal();
    } catch (_) {
      notification.error({ message: 'Failed to block dates' });
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const handleBulkUnblock = async (): Promise<void> => {
    const pid = resolvePropertyId();
    if (!pid || selectedDates.length === 0) return;
    const eligible = selectedDates.filter((d) => dayStatus(d) === 'BLOCKED');
    if (!eligible.length) {
      notification.warning({
        message: 'Nothing to unblock',
        description: 'None of the selected dates are blocked.',
      });
      return;
    }
    setCalendarActionLoading(true);
    try {
      const ranges = mergeContiguousDateRanges(eligible);
      await Promise.all(
        ranges.map((r) => CalendarService.unblockDates(pid, r)),
      );
      notification.success({ message: 'Dates unblocked' });
      await reloadCalendarData();
      setSelectedDates([]);
      closeActionsModal();
    } catch (_) {
      notification.error({ message: 'Failed to unblock dates' });
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const handleBulkMinNights = async (): Promise<void> => {
    const pid = resolvePropertyId();
    if (!pid || selectedDates.length === 0) return;
    if (!minNightsBulk || minNightsBulk < 1) {
      notification.warning({ message: 'Enter at least 1 night.' });
      return;
    }
    const eligible = selectedDates.filter((d) => dayStatus(d) !== 'BOOKED');
    if (!eligible.length) {
      notification.warning({
        message: 'No eligible dates',
        description: 'Booked dates cannot change minimum nights here.',
      });
      return;
    }
    setCalendarActionLoading(true);
    try {
      const ranges = mergeContiguousDateRanges(eligible);
      await Promise.all(
        ranges.map((r) =>
          CalendarService.updateRules(pid, {
            from: r.from,
            to: r.to,
            minNights: minNightsBulk,
          }),
        ),
      );
      notification.success({ message: 'Minimum nights updated' });
      await reloadCalendarData();
      setSelectedDates([]);
      closeActionsModal();
    } catch (_) {
      notification.error({ message: 'Failed to update minimum nights' });
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const handleBulkCancellationPolicy = async (): Promise<void> => {
    const pid = resolvePropertyId();
    if (!pid || selectedDates.length === 0) return;
    if (!cancellationPolicyBulk) {
      notification.warning({ message: 'Select a cancellation policy.' });
      return;
    }
    const eligible = selectedDates.filter((d) => dayStatus(d) !== 'BOOKED');
    if (!eligible.length) {
      notification.warning({
        message: 'No eligible dates',
        description: 'Booked dates cannot change cancellation policy here.',
      });
      return;
    }
    setCalendarActionLoading(true);
    try {
      const ranges = mergeContiguousDateRanges(eligible);
      // Backend applies the policy only to dates that have a price row and returns
      // the count it actually updated; sum those across the contiguous ranges.
      const results = await Promise.all(
        ranges.map((r) =>
          CalendarService.setCancellationPolicy(pid, {
            fromDate: r.from,
            toDate: r.to,
            cancellationPolicyCode: cancellationPolicyBulk,
          }),
        ),
      );
      const applied = results.reduce(
        (sum, res) => sum + (Number(res?.updatedDates) || 0),
        0,
      );
      notification.success({
        message: 'Cancellation policy updated',
        description: `Applied to ${applied} date${applied === 1 ? '' : 's'}.`,
      });
      await reloadCalendarData();
      setSelectedDates([]);
      closeActionsModal();
    } catch (_) {
      notification.error({ message: 'Failed to update cancellation policy' });
    } finally {
      setCalendarActionLoading(false);
    }
  };

  const handleClearSelection = (): void => {
    setSelectedDates([]);
    setBulkPrice('');
    closeActionsModal();
  };

  const handleDefaultPriceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = e.target.value;
    setDefaultPrice(value ? Number(value) : undefined);
  };

  const handleWeekendDefaultPriceChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = e.target.value;
    setWeekendDefaultPrice(value ? Number(value) : undefined);
  };

  /**
   * Submit handler for the Airbnb iCal availability link. Validates the URL and registers
   * a subscription on the backend; the scheduler picks it up within ~1 minute.
   */
  const handleIcalSubmit = async (): Promise<void> => {
    const raw = icalUrl.trim();
    if (!raw) return;
    try {
      new URL(raw);
    } catch {
      notification.warning({
        message: 'Invalid URL',
        description: 'Please paste a valid Airbnb iCal link (must start with https://).',
      });
      return;
    }
    if (!looksLikeAirbnbIcalUrl(raw)) {
      notification.warning({
        message: 'Not an iCal link',
        description: isAirbnbUrl(raw)
          ? "This looks like your Airbnb listing page, not the iCal calendar link. Tap the ⓘ above this field for steps to fetch the correct link (Settings → Availability → Connect another calendar), then paste it here."
          : 'Please paste the Airbnb iCal calendar link — it looks like https://www.airbnb.com/calendar/ical/....ics?s=...',
      });
      return;
    }
    if (!propertyId) {
      notification.error({
        message: 'Property not selected',
        description: 'Cannot register iCal sync without a property context.',
      });
      return;
    }
    setIcalSubmitting(true);
    try {
      await CalendarService.addIcalSubscription(propertyId, raw);
      setSavedIcalUrl(raw);
      notification.success({
        message: 'Calendar synced',
        description:
          'Your calendar availability should be successfully synced! In case of any issues, you will hear back from us.',
      });
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Could not register iCal sync. Please try again.';
      notification.error({ message: 'Sync failed', description: message });
    } finally {
      setIcalSubmitting(false);
    }
  };

  /**
   * Force-poll the existing iCal subscription so the host can pull fresh availability without
   * waiting for the 15-minute scheduler tick. No-ops if no subscription is saved yet.
   */
  const handleIcalReload = async (): Promise<void> => {
    if (!propertyId || !savedIcalUrl) return;
    setIcalReloading(true);
    try {
      await CalendarService.reloadIcalForProperty(propertyId);
      notification.success({
        message: 'Sync triggered',
        description: 'We have pulled the latest availability from your Airbnb calendar.',
      });
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Could not reload right now. Please try again in a moment.';
      notification.error({ message: 'Reload failed', description: message });
    } finally {
      setIcalReloading(false);
    }
  };

  /**
   * Generate (or fetch) the outbound Unpaqd iCal URL the host can paste into Airbnb / Vrbo /
   * Booking.com. Backend is idempotent — same URL returned across calls unless rotated.
   */
  const handleGenerateExportUrl = async (): Promise<void> => {
    if (!propertyId) {
      notification.error({
        message: 'Property not selected',
        description: 'Cannot generate an export link without a property context.',
      });
      return;
    }
    setExportGenerating(true);
    try {
      const res = await CalendarService.getOrCreateIcalExportToken(propertyId);
      setExportUrl(res.url);
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Could not generate iCal link. Please try again.';
      notification.error({ message: 'Generation failed', description: message });
    } finally {
      setExportGenerating(false);
    }
  };

  const handleCopyExportUrl = async (): Promise<void> => {
    if (!exportUrl) return;
    try {
      await navigator.clipboard.writeText(exportUrl);
      setExportCopied(true);
      window.setTimeout(() => setExportCopied(false), 2000);
    } catch {
      notification.warning({
        message: 'Copy failed',
        description: 'Please copy the link manually.',
      });
    }
  };

  /**
   * Submit the other-platform listing URL for pricing review. Brought in from main —
   * persists via the existing /syncAirbnb endpoint with syncMode=pricing.
   */
  const handlePricingUrlSubmit = async (): Promise<void> => {
    const raw = pricingUrl.trim();
    if (!raw) return;
    try {
      new URL(raw);
    } catch {
      notification.warning({
        message: 'Invalid URL',
        description: 'Please enter a valid listing URL.',
      });
      return;
    }
    const pid = resolvePropertyId();
    if (!pid) {
      notification.error({
        message: 'Property not selected',
        description: 'Cannot submit a pricing sync URL without a property context.',
      });
      return;
    }
    setPricingUrlLoading(true);
    try {
      const response = await CalendarService.syncAirbnbListing(pid, {
        airbnbListingUrl: raw,
        syncMode: 'pricing',
      });
      const responseMessage = response?.responseInformation?.responseMessage;
      // Backend distinguishes a well-formed listing URL that Airbnb has no data for
      // (delisted/expired) from a link that isn't in a format it can even scrape.
      if (responseMessage === 'LISTING_NOT_FOUND') {
        notification.warning({
          message: 'Listing not available',
          description:
            "This link isn't available for price sync — the listing may be expired or no longer live on Airbnb. Please check the listing and try again.",
        });
        return;
      }
      // Backend explicitly flags a recognized-but-unusable link (e.g. an Airbnb URL that
      // isn't the iCal export format) rather than silently queuing it for manual review.
      if (responseMessage === 'INVALID_LINK' || responseMessage === 'NOT_SUPPORTED') {
        notification.warning({
          message: 'Link not supported',
          description: isAirbnbUrl(raw)
            ? "This Airbnb link isn't in a format we can sync pricing from. Double-check it's the correct listing URL and try again."
            : "We couldn't recognize this listing URL. Please double-check the link and try again.",
        });
        return;
      }
      const result: 'SYNCED' | 'PENDING_REVIEW' =
        responseMessage === 'SYNCED' ? 'SYNCED' : 'PENDING_REVIEW';
      setPricingUrlResult(result);
      setSavedPricingUrl(raw);
      try {
        localStorage.setItem(`pricingSyncUrl_${pid}`, raw);
        localStorage.setItem(`pricingSyncStatus_${pid}`, result);
        // Resubmitting the link turns price sync back on (backend flips
        // airbnbPriceSyncEnabled -> true), so drop the local "disabled" marker left by the
        // unsync tool — otherwise the pre-fill effect would immediately blank this field again.
        localStorage.removeItem(`airbnbPriceSyncDisabled_${pid}`);
      } catch (_) {
        // localStorage unavailable — status just won't survive a reload
      }
    } catch (err: any) {
      notification.error({
        message: 'Could not sync pricing',
        description: getUserFriendlyError(
          err,
          'Could not submit this link. Please check it and try again.',
        ),
      });
    } finally {
      setPricingUrlLoading(false);
    }
  };

  const getDateRangeLabel = (): string => {
    if (!selectedDates.length) return '';
    const sorted = [...selectedDates].sort();
    const first = new Date(sorted[0] + 'T00:00:00');
    const last = new Date(sorted[sorted.length - 1] + 'T00:00:00');
    const fmtDay = (d: Date) =>
      d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    if (sorted.length === 1) return fmtDay(first);
    if (first.getMonth() === last.getMonth())
      return `${first.getDate()} – ${fmtDay(last)}`;
    return `${fmtDay(first)} – ${fmtDay(last)}`;
  };

  const getSelectedAvailabilityStatus = (): 'available' | 'blocked' | 'mixed' => {
    if (!selectedDates.length) return 'available';
    const statuses = selectedDates.map((d) => dayStatus(d));
    const allBlocked = statuses.every((s) => s === 'BLOCKED');
    const noneBlocked = statuses.every((s) => s !== 'BLOCKED');
    if (allBlocked) return 'blocked';
    if (noneBlocked) return 'available';
    return 'mixed';
  };

  const getSelectedPriceRange = (): { min: number; max: number } | null => {
    const priceValues = selectedDates
      .map((d) => getDisplayPriceForDate(new Date(d + 'T00:00:00'), d))
      .filter((p): p is number => p != null && p > 0);
    if (!priceValues.length) return null;
    return { min: Math.min(...priceValues), max: Math.max(...priceValues) };
  };

  const handleNumpadInput = (key: string): void => {
    if (key === '⌫') {
      setBulkPrice((prev) => prev.slice(0, -1));
    } else {
      setBulkPrice((prev) => {
        const next = prev + key;
        return next.length > 7 ? prev : next;
      });
    }
  };

  const isWeekendDate = (date: Date): boolean => {
    const day = date.getDay();
    return (
      (day === 5 && !!selectedWeekendDays.fri) ||
      (day === 6 && !!selectedWeekendDays.sat) ||
      (day === 0 && !!selectedWeekendDays.sun)
    );
  };

  const tileContent = ({ date, view }: TileContentArgs): JSX.Element | null => {
    if (view === 'month') {
      const dateString = formatDateString(date);
      const calendarDay = calendarData[dateString];
      const isFutureOrToday = date >= todayStart;
      const displayPrice = getDisplayPriceForDate(date, dateString);
      const displayMinNights = getDisplayMinNightsForDate(date, dateString);
      const blocked = calendarDay?.status === 'BLOCKED';
      const defaultMin = isWeekendDate(date) ? weekendMinNights : weekdayMinNights;
      const customMin =
        displayMinNights != null && Number(displayMinNights) !== Number(defaultMin);
      // PriceLabs-synced check-in/check-out restrictions — undefined (older cached
      // responses) means "no restriction data", not "restricted", so only show the
      // badge when the API explicitly said false.
      const checkInRestricted = calendarDay?.checkInAllowed === false;
      const checkOutRestricted = calendarDay?.checkOutAllowed === false;
      const restrictionLabel =
        checkInRestricted && checkOutRestricted
          ? 'No check-in/out'
          : checkInRestricted
            ? 'No check-in'
            : checkOutRestricted
              ? 'No check-out'
              : '';

      if (!isFutureOrToday) return null;
      const stay = stayAt(dateString, stayIndex, todayYmd);
      if (stay) {
        // a booked night shows its piece of the booking bar instead of a price
        return (
          <>
            <StayBar $start={stay.startsHere} $end={stay.endsHere} $other={stay.kind === 'other'} />
            {stay.showLabel && (
              <StayLabel $start={stay.startsHere} $span={stay.spanInRow}>
                {stay.kind === 'unpaqd' && <span className="avatar">{stay.initial}</span>}
                <span className="text">{stay.label}</span>
              </StayLabel>
            )}
          </>
        );
      }
      return (
        <TileInfo>
          {displayPrice != null ? (
            <span className={`price${blocked ? ' blocked' : ''}`}>
              ₹{Number(displayPrice).toLocaleString('en-IN')}
            </span>
          ) : (
            <span style={{ height: 11 }} />
          )}
          {restrictionLabel ? (
            <span
              className="meta restricted"
              title={
                checkInRestricted && checkOutRestricted
                  ? 'Check-in and check-out restricted (set in PriceLabs)'
                  : checkInRestricted
                    ? 'Check-in restricted (set in PriceLabs)'
                    : 'Check-out restricted (set in PriceLabs)'
              }
            >
              {restrictionLabel}
            </span>
          ) : customMin ? (
            <span className="meta" title="Minimum stay (nights)">
              Min {displayMinNights}
            </span>
          ) : null}
        </TileInfo>
      );
    }
    return null;
  };

  const tileClassName = ({ date, view }: TileContentArgs): string | null => {
    if (view === 'month') {
      const dateString = formatDateString(date);
      const calendarDay = calendarData[dateString];
      const isPastDate = date < todayStart;
      const isSelected = selectedDates.includes(dateString);

      const classes: string[] = [];
      if (isPastDate) classes.push('pc-past');
      if (calendarDay?.status === 'BLOCKED') classes.push('pc-blocked');
      if (calendarDay?.status === 'BOOKED') classes.push('pc-booked');
      if (isSelected) classes.push('pc-selected');

      return classes.join(' ');
    }
    return null;
  };

  const [priceTab, setPriceTab] = useState<'weekday' | 'weekend'>('weekday');
  const minNightsValue = priceTab === 'weekday' ? weekdayMinNights : weekendMinNights;
  const setMinNightsValue = priceTab === 'weekday' ? setWeekdayMinNights : setWeekendMinNights;
  const priceMissing = !defaultPrice;

  const weekendChips: { key: 'fri' | 'sat' | 'sun'; label: string }[] = [
    { key: 'fri', label: 'Friday' },
    { key: 'sat', label: 'Saturday' },
    { key: 'sun', label: 'Sunday' },
  ];

  return (
    <>
      <Group title="Calendar pricing">
        <div>
          <ConfigHeader>
            <span className="label">Pricing configuration</span>
            <Segmented>
              {(['weekday', 'weekend'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  aria-pressed={priceTab === tab}
                  onClick={() => setPriceTab(tab)}
                >
                  {tab === 'weekday' ? 'Weekday' : 'Weekend'}
                </button>
              ))}
            </Segmented>
          </ConfigHeader>
          {/* Only relevant once the host is setting weekend pricing, so it is only offered here. */}
          {priceTab === 'weekend' ? (
            <div style={{ marginBottom: 16 }}>
              <FieldLabel style={{ fontSize: 13 }}>
                Choose which days you want to treat as weekend
              </FieldLabel>
              <ChipRow>
                {weekendChips.map(({ key, label }) => (
                  <Chip
                    key={key}
                    type="button"
                    $active={!!selectedWeekendDays[key]}
                    $soft
                    aria-pressed={!!selectedWeekendDays[key]}
                    onClick={() => toggleWeekendDay(key)}
                  >
                    {label}
                  </Chip>
                ))}
              </ChipRow>
            </div>
          ) : null}
          <TwoCol>
            <label style={{ display: 'block' }}>
              <SubLabel>Minimum bookable nights</SubLabel>
              <TextInput
                type="number"
                min={1}
                required
                placeholder={priceTab === 'weekday' ? 'e.g. 1' : 'e.g. 2'}
                value={minNightsValue >= 1 ? minNightsValue : ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : 0;
                  setMinNightsValue(Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
                }}
                onBlur={() => {
                  if (!minNightsValue || minNightsValue < 1) {
                    setMinNightsValue(1);
                  }
                }}
              />
            </label>
            <div>
              <SubLabel>
                Default price per night{' '}
                {priceTab === 'weekday' ? (
                  <Required aria-hidden="true">*</Required>
                ) : null}
              </SubLabel>
              <AdornedInput>
                <span className="pre">₹</span>
                {priceTab === 'weekday' ? (
                  <TextInput
                    key="wd"
                    type="number"
                    value={defaultPrice || ''}
                    onChange={handleDefaultPriceChange}
                    placeholder="Set default price for next 3 months"
                    style={{ paddingLeft: 36 }}
                  />
                ) : (
                  <TextInput
                    key="we"
                    type="number"
                    value={weekendDefaultPrice || ''}
                    onChange={handleWeekendDefaultPriceChange}
                    placeholder="Set extra price for weekends"
                    style={{ paddingLeft: 36 }}
                  />
                )}
              </AdornedInput>
              {priceTab === 'weekday' && fetchingDefaultPrice && !defaultPrice ? (
                <NoteLine>
                  Fetching your Airbnb price… it will appear here shortly. You can also
                  enter it manually.
                </NoteLine>
              ) : null}
              {priceTab === 'weekend' && priceMissing ? (
                <RequiredChoice message="Switch to Weekday to enter the required nightly price." />
              ) : null}
            </div>
          </TwoCol>
        </div>

        {/* ===== Security Deposit ===== */}
        <SecurityDepositCard
          securityDeposit={securityDeposit}
          setSecurityDeposit={setSecurityDeposit}
        />
      </Group>

      <Group
        title="Sync your calendar"
        info="Sync the calendar with your Airbnb iCal to avoid double bookings."
      >
        {/* ===== Calendar Availability (iCal) ===== */}
        <Field
          label="Calendar availability (Airbnb iCal link)"
          info={
            <StepsList>
              <span>Go to your listing calendar</span>
              <span>Go to Settings → Availability</span>
              <span>Scroll down to Connect another calendar → Connect to another website</span>
              <span>Copy the calendar link available, and paste it here</span>
            </StepsList>
          }
        >
          <TextInput
            id="ical-url"
            type="url"
            inputMode="url"
            placeholder="https://www.airbnb.com/calendar/ical/…"
            value={icalUrl}
            onChange={(e) => setIcalUrl(e.target.value.trimStart())}
            autoComplete="off"
            spellCheck={false}
          />
          {/* Reload appears whenever a URL is saved — lets host force a fresh poll without
              waiting for the 15-min scheduler tick. Submit appears only when the URL changed. */}
          {(savedIcalUrl !== '' && icalUrl.trim() === savedIcalUrl.trim()) ||
          (icalUrl.trim() !== '' && icalUrl.trim() !== savedIcalUrl.trim()) ? (
            <ActionsRow>
              {savedIcalUrl !== '' && icalUrl.trim() === savedIcalUrl.trim() && (
                <OutlineButton type="button" onClick={handleIcalReload} disabled={icalReloading}>
                  {icalReloading ? 'Resyncing…' : 'Resync'}
                </OutlineButton>
              )}
              {icalUrl.trim() !== '' && icalUrl.trim() !== savedIcalUrl.trim() && (
                <PrimaryButton type="button" onClick={handleIcalSubmit} disabled={icalSubmitting}>
                  {icalSubmitting ? 'Submitting…' : 'Submit'}
                </PrimaryButton>
              )}
            </ActionsRow>
          ) : null}
        </Field>

        {/* ===== Export Unpaqd iCal link (outbound feed) ===== */}
        <Field
          label="Export Unpaqd iCal link"
          info="Sync your Unpaqd calendar for the listing with other platforms to avoid double bookings."
        >
          <InlineAction>
            <input
              readOnly
              value={exportUrl}
              placeholder="Tap Generate to create your link"
              aria-label="Unpaqd iCal export URL"
              onFocus={(e) => e.currentTarget.select()}
            />
            {exportUrl ? (
              <GhostButton
                type="button"
                style={{ height: 36, padding: '0 14px', fontSize: 13 }}
                onClick={handleCopyExportUrl}
              >
                {exportCopied ? (
                  <>
                    <FiCheck size={14} /> Copied
                  </>
                ) : (
                  <>
                    <FiCopy size={14} /> Copy
                  </>
                )}
              </GhostButton>
            ) : (
              <PrimaryButton
                type="button"
                style={{ height: 36, padding: '0 14px', fontSize: 13 }}
                onClick={handleGenerateExportUrl}
                disabled={exportGenerating}
              >
                {exportGenerating ? 'Generating…' : 'Generate'}
              </PrimaryButton>
            )}
          </InlineAction>
        </Field>
      </Group>

      <Group title="Calendar Pricing">
        {/* ── Calendar Pricing (wired to /syncAirbnb with syncMode=pricing) ─ */}
        <Field
          label="Your listing URL"
          info="Share the URL of this property's listing on any other platform to sync pricing."
        >
          <TextInput
            id="calendar-pricing-url"
            type="url"
            inputMode="url"
            placeholder="https://www.airbnb.com/rooms/…"
            value={pricingUrl}
            onChange={(e) => setPricingUrl(e.target.value.trimStart())}
            autoComplete="off"
            spellCheck={false}
          />

          {/* Status reflects the last *submitted* value — reverts to the plain note as soon as
              the host edits the field away from what was actually saved. */}
          {pricingUrlResult && pricingUrl.trim() === savedPricingUrl.trim() ? (
            <StatusBanner>
              {pricingUrlResult === 'SYNCED' ? (
                <>
                  <p>Pricing synced</p>
                  <p>
                    We detected an Airbnb listing URL and synced this property&apos;s
                    pricing right away.
                  </p>
                </>
              ) : (
                <>
                  <p>URL submitted</p>
                  <p>
                    Once reviewed, our team will confirm whether price sync integration is
                    available.
                  </p>
                </>
              )}
            </StatusBanner>
          ) : (
            <NoteLine>
              <strong>Note:</strong> Price sync may not be supported for every platform.
              Once reviewed, our team will confirm whether integration is available.
            </NoteLine>
          )}

          {pricingUrl.trim() !== '' && pricingUrl.trim() !== savedPricingUrl.trim() && (
            <ActionsRow>
              <PrimaryButton
                type="button"
                onClick={() => void handlePricingUrlSubmit()}
                disabled={pricingUrlLoading}
              >
                {pricingUrlLoading ? PRICING_SYNC_MESSAGES[pricingSyncMsgIndex] : 'Submit'}
              </PrimaryButton>
              {pricingUrlLoading && (
                <NoteLine style={{ width: '100%' }}>
                  {isAirbnbUrl(pricingUrl)
                    ? 'Airbnb links can take 1–2 minutes to sync pricing — please stay on this page.'
                    : "This won't take long — please stay on this page."}
                </NoteLine>
              )}
            </ActionsRow>
          )}
        </Field>

        <NoteLine style={{ fontSize: 13.5 }}>
          Select one or more dates to manage availability, minimum nights, or special
          pricing.
        </NoteLine>

        <CalendarSkin>
          {isLoadingCalendar ? (
            <LoadingNote>Loading calendar...</LoadingNote>
          ) : (
            <Calendar
              onChange={handleDateClick}
              value={undefined}
              tileContent={tileContent}
              tileClassName={tileClassName}
              activeStartDate={activeStartDate}
              onActiveStartDateChange={({ activeStartDate: next }) => {
                if (next) setActiveStartDate(next);
              }}
              minDate={todayStart}
              maxDate={calendarMaxDate}
              showNavigation
              showNeighboringMonth={false}
              prev2Label={null}
              next2Label={null}
              prevLabel="‹"
              nextLabel="›"
              calendarType="gregory"
              locale="en-GB"
              formatShortWeekday={(_locale, date) =>
                ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()]
              }
            />
          )}
        </CalendarSkin>
      </Group>

      {selectedDates.length > 0 && (() => {
        const availStatus = getSelectedAvailabilityStatus();
        const priceRange = getSelectedPriceRange();
        const label = getDateRangeLabel();
        return (
          <Sheet role="dialog" aria-label="Edit selected dates">
            <SheetHeader>
              <span className="pill">{label}</span>
              <button
                type="button"
                className="close"
                onClick={handleClearSelection}
                aria-label="Dismiss"
              >
                ×
              </button>
            </SheetHeader>

            <SheetRow>
              <span className="left">
                <span className={`dot ${availStatus}`} />
                <span className="label">
                  {availStatus === 'blocked'
                    ? 'Blocked'
                    : availStatus === 'mixed'
                      ? 'Mixed'
                      : 'Available'}
                </span>
              </span>
              <ToggleGroup>
                <button
                  type="button"
                  className={`block${availStatus === 'blocked' ? ' on' : ''}`}
                  onClick={handleBulkBlock}
                  disabled={calendarActionLoading}
                  title="Block dates"
                >
                  ✕
                </button>
                <button
                  type="button"
                  className={`unblock${availStatus === 'available' ? ' on' : ''}`}
                  onClick={handleBulkUnblock}
                  disabled={calendarActionLoading}
                  title="Unblock dates"
                >
                  ✓
                </button>
              </ToggleGroup>
            </SheetRow>

            <SheetRow
              as="button"
              type="button"
              $button
              onClick={() => {
                if (!bulkPrice && priceRange) {
                  setBulkPrice(String(priceRange.max));
                }
                setPriceInputOpen(true);
              }}
            >
              <span className="left">
                <span className="label">Price</span>
                {priceRange && priceRange.min !== priceRange.max && (
                  <span className="sub">Multiple prices</span>
                )}
              </span>
              <span className="right">
                {priceRange ? (
                  <span className="value">
                    {priceRange.min === priceRange.max
                      ? `₹${priceRange.min.toLocaleString('en-IN')}`
                      : `₹${priceRange.min.toLocaleString('en-IN')} – ₹${priceRange.max.toLocaleString('en-IN')}`}
                  </span>
                ) : (
                  <span className="value empty">Set price</span>
                )}
                <FiChevronRight size={18} />
              </span>
            </SheetRow>

            <SheetRow
              as="button"
              type="button"
              $button
              onClick={() => setCustomSettingsOpen(true)}
            >
              <span className="label">Custom settings</span>
              <span className="right">⋮</span>
            </SheetRow>
          </Sheet>
        );
      })()}

      {bookedDetailModal.open && bookedDetailModal.dateStr && (
        <Overlay
          $bottom
          onClick={() => setBookedDetailModal({ open: false, dateStr: null })}
          role="presentation"
        >
          <BookedModal
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3>
              {new Date(bookedDetailModal.dateStr + 'T00:00:00').toLocaleDateString(
                'en-IN',
                {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                },
              )}
            </h3>
            <p className="lead">This night is booked.</p>
            {(() => {
              const list = bookingsByDate[bookedDetailModal.dateStr] || [];
              const pending = pendingByDate[bookedDetailModal.dateStr] || [];
              const dropped = droppedByDate[bookedDetailModal.dateStr] || [];
              const day = calendarData[bookedDetailModal.dateStr];
              const kind = classifyBookedNight(day, list.length ? list : pending.length ? pending : dropped, hostBookingsLoaded);
              if (!list.length && pending.length) {
                // our own booking whose payment is still going on: the calendar already holds its nights
                return (
                  <>
                    {pending.map((raw, idx) => {
                      const b = raw as Record<string, unknown>;
                      return (
                        <div className="card" key={`pending-${String(b.bookingId ?? b.id ?? idx)}-${idx}`}>
                          <div className="guest">{resolveGuestName(b)}</div>
                          <div className="line">Payment pending</div>
                          {(b.checkIn != null || b.checkOut != null) && (
                            <div className="stay">
                              Stay: {String(b.checkIn ?? '').slice(0, 10)} →{' '}
                              {String(b.checkOut ?? '').slice(0, 10)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                );
              }
              if (!list.length && dropped.length) {
                // a cancelled, failed or expired booking of ours: the calendar still marks the night booked (not released yet)
                return (
                  <>
                    {dropped.map((raw, idx) => {
                      const b = raw as Record<string, unknown>;
                      return (
                        <div className="card" key={`dropped-${String(b.bookingId ?? b.id ?? idx)}-${idx}`}>
                          <div className="guest">{resolveGuestName(b)}</div>
                          <div className="line">Cancelled Unpaqd booking, night still marked booked</div>
                          <p className="muted" style={{ margin: 0 }}>
                            This booking is {String(b.status ?? 'not active').toLowerCase().replace(/_/g, ' ')}, but the calendar has not released the night yet.
                          </p>
                        </div>
                      );
                    })}
                  </>
                );
              }
              if (!list.length) {
                if (kind === 'other') {
                  const stretch = bookedStretch(bookedDetailModal.dateStr, calendarData);
                  const label = (d: string): string =>
                    new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
                  return (
                    <div className="card">
                      <div className="guest">Booked on another platform</div>
                      <p className="line" style={{ margin: '0 0 6px' }}>
                        {stretch.nights > 1
                          ? `Dates booked: ${label(stretch.from)} to ${label(stretch.to)} (${stretch.nights} nights)`
                          : `Date booked: ${label(stretch.from)}`}
                      </p>
                      <p className="muted" style={{ margin: 0 }}>
                        Booking unavailable: the guest and price are not shown for bookings made outside Unpaqd.
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="card">
                    <div className="guest">{kind === 'unpaqd' ? 'Booked on Unpaqd' : 'Booked'}</div>
                    <p className="line" style={{ margin: 0 }}>
                      We couldn&apos;t load the guest details for this booking. Please reload the page.
                    </p>
                  </div>
                );
              }
              return (
                <>
                  {list.map((raw, idx) => {
                    const b = raw as Record<string, unknown>;
                    const guest = resolveGuestName(b);
                    const rate = resolveBookedNightRateLabel(b);
                    const total = bookingTotalAmount(b);
                    return (
                      <div className="card" key={`${String(b.bookingId ?? b.id ?? idx)}-${idx}`}>
                        <div className="guest">{guest}</div>
                        <div className="line">
                          Booked at: <strong>{rate}</strong>
                        </div>
                        {total > 0 && (
                          <div className="muted">
                            Booking total: {formatCurrencyAmount.format(total)}
                          </div>
                        )}
                        {(b.checkIn != null || b.checkOut != null) && (
                          <div className="stay">
                            Stay: {String(b.checkIn ?? '').slice(0, 10)} →{' '}
                            {String(b.checkOut ?? '').slice(0, 10)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              );
            })()}
            <button
              type="button"
              className="close"
              onClick={() => setBookedDetailModal({ open: false, dateStr: null })}
            >
              Close
            </button>
          </BookedModal>
        </Overlay>
      )}

      {priceInputOpen && selectedDates.length > 0 && (
        <FullScreen role="dialog" aria-modal="true">
          <div className="inner">
            <div className="head">
              <span className="title">{getDateRangeLabel()}</span>
              <button
                type="button"
                className="close"
                onClick={() => setPriceInputOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="display">
              <span className="cur">₹</span>
              <input
                className="amount"
                type="number"
                inputMode="numeric"
                min={0}
                value={bulkPrice}
                placeholder="0"
                onChange={(e) => setBulkPrice(e.target.value.replace(/[^0-9]/g, ''))}
                autoFocus
              />
            </div>
            <div className="hint">Tap numbers below to set the price per night</div>
            <button
              type="button"
              className="save"
              onClick={() => void handleBulkSetPrice()}
              disabled={!bulkPrice || calendarActionLoading}
            >
              {calendarActionLoading ? 'Saving…' : 'Save'}
            </button>
            <div className="pad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) => (
                <button
                  key={i}
                  type="button"
                  className={`key${key === '' ? ' empty' : ''}${key === '⌫' ? ' back' : ''}`}
                  onClick={() => key !== '' && handleNumpadInput(key)}
                  disabled={key === ''}
                  aria-label={key === '⌫' ? 'Backspace' : key}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        </FullScreen>
      )}

      {customSettingsOpen && selectedDates.length > 0 && (
        <Overlay onClick={() => setCustomSettingsOpen(false)} role="presentation">
          <Modal onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="head">
              <span>Custom settings</span>
              <button
                type="button"
                className="close"
                onClick={() => setCustomSettingsOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="sub">{getDateRangeLabel()}</p>
            <div className="row">
              <span>Minimum nights</span>
              <div className="stepper">
                <button
                  type="button"
                  onClick={() => setMinNightsBulk((p) => Math.max(1, p - 1))}
                  disabled={minNightsBulk <= 1}
                  aria-label="Decrease minimum nights"
                >
                  −
                </button>
                <span>{minNightsBulk}</span>
                <button
                  type="button"
                  onClick={() => setMinNightsBulk((p) => p + 1)}
                  aria-label="Increase minimum nights"
                >
                  +
                </button>
              </div>
            </div>
            <button
              type="button"
              className="apply"
              onClick={() => {
                void handleBulkMinNights();
                setCustomSettingsOpen(false);
              }}
              disabled={calendarActionLoading || minNightsBulk < 1}
            >
              Apply minimum nights
            </button>
            <div className="divider" />
            <div className="block">
              <label className="block-label">
                Cancellation policy
              </label>
              <CancellationPolicyPicker
                value={cancellationPolicyBulk}
                catalog={cancellationCatalog}
                onChange={setCancellationPolicyBulk}
              />
            </div>
            <button
              type="button"
              className="apply"
              onClick={() => {
                void handleBulkCancellationPolicy();
                setCustomSettingsOpen(false);
              }}
              disabled={calendarActionLoading || !cancellationPolicyBulk}
            >
              Apply cancellation policy
            </button>
          </Modal>
        </Overlay>
      )}
    </>
  );
};

export default CalendarPricing;
